import React, { useEffect, useState } from "react";
import { HandHeart, Loader2, Users, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ChatAvatar from "@/components/ChatAvatar";
import ContextualHint from "@/components/ContextualHint";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function PrayerWall() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/prayers/wall").then((r) => { setList(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Remove this prayer from the wall?")) return;
    try { await api.delete(`/prayers/${id}`); toast.success("Prayer removed"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const saveEdit = async () => {
    if (!editing.text.trim()) { toast.error("Please enter the prayer"); return; }
    try {
      await api.put(`/prayers/${editing.id}`, { text: editing.text });
      toast.success("Prayer updated");
      setEditing(null); load();
    } catch (e) { toast.error("Could not update"); }
  };

  const togglePray = async (p) => {
    if (!user) { toast.error("Please sign in to pray for others"); return; }
    setList((prev) => prev.map((x) => x.id === p.id ? { ...x, i_prayed: !x.i_prayed, prayed_count: x.prayed_count + (x.i_prayed ? -1 : 1) } : x));
    try {
      const { data } = await api.post(`/prayers/${p.id}/pray`);
      setList((prev) => prev.map((x) => x.id === p.id ? { ...x, i_prayed: data.i_prayed, prayed_count: data.prayed_count } : x));
    } catch (e) {
      toast.error("Could not record that");
      load();
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10 max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <Users className="w-6 h-6 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Prayer Wall</h1>
      </div>
      <p className="text-zinc-400 mb-8">
        Stand together in prayer. Tap "I prayed for this" to let a fellow believer know they're covered.
        {user && <> Want to share yours? <Link to="/prayer" className="text-[#E6B34A] font-semibold">Post a request</Link>.</>}
      </p>

      {user && (
        <ContextualHint storageKey="tip_prayerwall_seen" testid="tip-prayerwall">
          Tap <span className="font-semibold text-[#E6B34A]">"I prayed for this"</span> on any request to encourage them — they'll be notified that someone prayed. You can also post your own request anytime.
        </ContextualHint>
      )}

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div> :
        list.length === 0 ? <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="wall-empty">No public prayer requests yet. Be the first to share one.</p> :
        <div className="space-y-4" data-testid="prayer-wall-list">
          {list.map((p) => {
            const canManage = user && (user.role === "admin" || (p.wall_user_id && p.wall_user_id === user.id));
            return (
            <div key={p.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid={`wall-prayer-${p.id}`}>
              <div className="flex items-start gap-3">
                <HandHeart className="w-5 h-5 text-[#E6B34A] shrink-0 mt-0.5" />
                <p className="text-zinc-200 flex-1">{p.text}</p>
                {canManage && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => setEditing({ id: p.id, text: p.text })} data-testid={`wall-edit-${p.id}`} title="Edit" className="p-1.5 rounded-lg bg-white/5 text-zinc-400 hover:text-[#E6B34A] hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p.id)} data-testid={`wall-del-${p.id}`} title="Delete" className="p-1.5 rounded-lg bg-white/5 text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  <ChatAvatar userId={p.wall_user_id} name={p.user_name} size={22} />
                  — {p.user_name || "A member"}
                </div>
                <button
                  onClick={() => togglePray(p)}
                  data-testid={`pray-btn-${p.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${p.i_prayed ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#E6B34A]/10 text-[#E6B34A] hover:bg-[#E6B34A]/20"}`}
                >
                  <HandHeart className="w-4 h-4" />
                  {p.i_prayed ? "You prayed" : "I prayed for this"}
                  <span className="ml-1 tabular-nums" data-testid={`pray-count-${p.id}`}>{p.prayed_count}</span>
                </button>
              </div>
            </div>
            );
          })}
        </div>}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="wall-edit-dialog">
          <DialogHeader><DialogTitle className="font-display font-extrabold text-2xl tracking-tight">Edit Prayer</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <Textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid="edit-wall-text" className="bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] min-h-[120px]" />
              <button onClick={saveEdit} data-testid="save-wall-edit" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">Save changes</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
